import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { fileUrl } from "@/lib/activity";
import { uploadImage } from "@/lib/upload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const TITLES = {
  terkonfirmasi: ["Konfirmasi Kegiatan", "Pastikan pemateri/petugas sudah mengonfirmasi kesediaan hadir. Setelah disimpan, info agenda otomatis dikirim ke Grup WA jamaah dan dokumen PDF tersedia."],
  dibatalkan: ["Batalkan Kegiatan", "Pemberitahuan pembatalan beserta alasannya akan dikirim otomatis ke Grup WA jamaah."],
  selesai: ["Laporan Kegiatan Selesai", "Isi laporan pasca kegiatan. Setelah disimpan, ringkasan dikirim otomatis ke Grup WA jamaah."],
};

export const StatusDialog = ({ status, activity, onClose, onSubmit }) => {
  const [agree, setAgree] = useState(false);
  const [reason, setReason] = useState("");
  const [report, setReport] = useState({ attendance_count: activity?.report?.attendance_count ?? "", summary: activity?.report?.summary || "", photos: activity?.report?.photos || [], notes: activity?.report?.notes || "" });
  const [busy, setBusy] = useState(false);
  if (!status) return null;
  const [title, desc] = TITLES[status];

  const addPhoto = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const paths = [];
      for (const f of Array.from(files)) paths.push(await uploadImage(f, "dokumentasi"));
      setReport((r) => ({ ...r, photos: [...r.photos, ...paths] }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status !== "selesai" && !agree) return toast.error("Centang persetujuan terlebih dahulu");
    if (status === "dibatalkan" && !reason.trim()) return toast.error("Alasan pembatalan wajib diisi");
    setBusy(true);
    try {
      await onSubmit({ status, confirmed: agree, reason, report: status === "selesai" ? { ...report, attendance_count: Number(report.attendance_count || 0) } : undefined });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`status-dialog-${status}`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-xl bg-[#fcfdf9] border border-emerald-900/10 p-3 text-sm"><b>{activity.title}</b><br /><span className="text-slate-500">{activity.date} · {activity.time || "-"} WIB · {activity.location}</span></div>
          {status === "dibatalkan" && (
            <div className="space-y-1.5"><Label>Alasan Pembatalan <span className="text-red-600">*</span></Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: pemateri berhalangan hadir" data-testid="cancel-reason-input" /></div>
          )}
          {status === "selesai" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Jumlah Jamaah Hadir <span className="text-red-600">*</span></Label><Input type="number" min={0} required value={report.attendance_count} onChange={(e) => setReport({ ...report, attendance_count: e.target.value })} data-testid="report-count" /></div>
              </div>
              <div className="space-y-1.5"><Label>Ringkasan Materi</Label><Textarea rows={3} value={report.summary} onChange={(e) => setReport({ ...report, summary: e.target.value })} data-testid="report-summary" /></div>
              <div className="space-y-1.5">
                <Label>Foto Dokumentasi</Label>
                <div className="flex flex-wrap gap-2">
                  {report.photos.map((p) => (
                    <div key={p} className="relative h-20 w-20"><img src={fileUrl(p)} alt="" className="h-20 w-20 rounded-xl object-cover" /><button type="button" onClick={() => setReport((r) => ({ ...r, photos: r.photos.filter((x) => x !== p) }))} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center"><X size={11} /></button></div>
                  ))}
                  <label className="h-20 w-20 rounded-xl border border-dashed border-emerald-900/25 flex items-center justify-center cursor-pointer hover:bg-emerald-50 text-slate-400">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhoto(e.target.files)} data-testid="report-photo-input" />
                  </label>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Catatan Lain</Label><Textarea rows={2} value={report.notes} onChange={(e) => setReport({ ...report, notes: e.target.value })} data-testid="report-notes" /></div>
            </>
          )}
          {status !== "selesai" && (
            <label className="flex items-start gap-3 rounded-xl border border-emerald-900/10 p-3 text-sm cursor-pointer">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="status-agree-checkbox" />
              <span>{status === "terkonfirmasi" ? "Pemateri/petugas telah mengonfirmasi kesediaan hadir dan jadwal kegiatan ini dipastikan berjalan." : "Saya memahami kegiatan ini akan dibatalkan dan pemberitahuan dikirim ke jamaah."}</span>
            </label>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={onClose} data-testid="status-dialog-cancel">Batal</Button>
            <Button type="submit" disabled={busy} className={`rounded-full ${status === "dibatalkan" ? "bg-red-600 hover:bg-red-700" : "emerald-bg hover:bg-[#145c48]"}`} data-testid="status-dialog-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : "Simpan & Kirim WA"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
